import React, { useState } from "react";
import ShowErrors from "./ShowErrors";
import axios from "axios";
import {
  Grid,
  makeStyles,
  Select,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  MenuItem,
  Button,
} from "@material-ui/core";
import LoadingIndicator from "./LoadingIndicator";
import { getPaymentTypeList, getPaymentStatusList } from "../services";
// import { tokenUtil } from "@acclaim/udaf-kit";

import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  labelHeight: {
    fontSize: "0.7rem",
    padding: `${theme.spacing(1)}px 0px`,
  },

  dateTextField: {
    width: 100,
    margin: 0,
    "& .MuiInputBase-root": {
      padding: `0px ${theme.spacing(1)}px`,
      fontSize: "0.7rem",
    },
    "& .MuiOutlinedInput-inputMarginDense": {
      padding: `0px ${theme.spacing(1)}px`,
    },
  },

  textField: {
    width: 200,
    margin: 0,
    "& .MuiInputBase-root": {
      padding: `4px ${theme.spacing(0)}px`,
      fontSize: "1rem",
    },
    "& .MuiOutlinedInput-inputMarginDense": {
      padding: `0px ${theme.spacing(1)}px`,
    },
  },
  headerStyle: {
    marginLeft: "8px",
    margingBottom: "4px",
    marginTop: "4px",
    // backgroundColor: "#e3f2fd",
    backgroundColor: "#e2efd9",
  },

  selectInBlock: {
    display: "inline-block",
    padding: theme.spacing(1),
  },

  checkBox: {
    display: "inline-block",
    padding: theme.spacing(1),
    "& .MuiIconButtonBase-root": {
      padding: "0px",
    },
    "& .MuiIconButton-label": {
      color: theme.palette.primary.light,
    },
    "& .MuiTypography-body1": {
      fontSize: "0.8rem",
    },
  },
}));

const EditPayment = ({
  selectedRows,
  openPopup,
  setOpenPopup,
  reconciledBy,
  editPaymentApiUrl,
  handleSearchFormSubmit,
  disableEditPaymentBtn,
  createNetInspectionApiClient,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const convertIsoDateStringToRegularDateString = (dateString) => {
    if (dateString === "") {
      return "";
    }

    // dataString format: 2023-07-18T00:00:00
    const bits = dateString.substring(0, 10).split("-");
    // biits [0] = year bits[1] = month bits[2] = day
    const formattedDate = `${bits[0]}-${bits[1]}-${bits[2]}`;
    return formattedDate;
  };

  const convertPaymentStatus = (statusString) => {
    if (statusString === "") {
      return "";
    }

    switch (statusString.toUpperCase()) {
      case "PENDING":
        return "Pending";
      case "COMPLETED":
        return "Completed";
      case "EXEMPT":
        return "Exempt";
      case "UNPAID":
        return "Unpaid";
      case "REFUNDED":
        return "Refunded";
      default:
        return statusString;
    }
  };

  // console.log("EditPayment", selectedRows);

  const [paymentDate, setPaymentDate] = useState(
    selectedRows.length === 1
      ? convertIsoDateStringToRegularDateString(
          selectedRows[0]?.payment_date
        ) ?? ""
      : convertIsoDateStringToRegularDateString(
          new Date().toISOString().substring(0, 10)
        )
  );
  const [paymentType, setPaymentType] = useState(
    selectedRows.length === 1 ? selectedRows[0]?.payment_type ?? "" : ""
  );
  const [paymentStatus, setPaymentStatus] = useState(
    selectedRows.length === 1
      ? convertPaymentStatus(selectedRows[0]?.payment_status) ?? ""
      : ""
  );
  const [checkNumber, setCheckNumber] = useState(
    selectedRows.length === 1 ? selectedRows[0]?.check_number ?? "" : ""
  );
  const [checkPayee, setCheckPayee] = useState(
    selectedRows.length === 1 ? selectedRows[0]?.check_payee ?? "" : ""
  );
  const [payerName, setPayerName] = useState(
    selectedRows.length === 1 ? selectedRows[0]?.payer_name ?? "" : ""
  );
  // const [editPaymentSubmitError, setEditPaymentSubmitError] = useState("");

  const initialPaymentType = selectedRows[0]?.payment_type ?? "";

  const handlePaymentTypeChange = (event) => {
    // console.log("PaymentType: ", event.target.value);
    setPaymentType(event.target.value);
  };

  const handlePaymentStatusChange = (event) => {
    setPaymentStatus(event.target.value);
  };

  const handleCheckNumberChange = (event) => {
    setCheckNumber(event.target.value);
  };

  const handlePayerNameChange = (event) => {
    setPayerName(event.target.value);
  };

  const handleCheckPayeeChange = (event) => {
    setCheckPayee(event.target.value);
  };

  const editPaymentPostDataUrl = editPaymentApiUrl();
  const postPaymentData = async (dataToPost) => {
    // console.log("Edit payment data to post:", dataToPost);
    setLoading(true);
    let baseUrl = `${editPaymentPostDataUrl}`;
    try {
      setLoading(true);
      // reset error before submitting
      // setEditPaymentSubmitError("");

      let response = await createNetInspectionApiClient().post(
        `${baseUrl}`,
        dataToPost
      );

      if (response && (response.data ?? "false").toLowerCase() === "true") {
        enqueueSnackbar("Edit payment was successful", { variant: "success" });
        clearData();

        disableEditPaymentBtn(true);
        handleSearchFormSubmit();
      } else {
        enqueueSnackbar(
          "An error occured editing payments, please refresh data and try again",
          { variant: "error" }
        );
      }
      // set the local state data
      setLoading(false);
    } catch (err) {
      // setEditPaymentSubmitError(err);
      setLoading(false);
      enqueueSnackbar(
        (err && err.message) || "Oops! Something went wrong. Please try again!",
        { variant: "error" }
      );
    } finally {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dataNotValid()) {
      // console.log("Validation passed");
      /*
     	_payment_id integer,
	_payment_date timestamp with time zone,
	_payment_type character varying,
	_payment_status character varying,
	_inspection_id integer,
	_inspection_type character varying,
	_modified_by_user character varying,
	_check_number character varying DEFAULT NULL::character varying,
	_check_payee character varying DEFAULT NULL::character varying,
	_payer_name character varying DEFAULT NULL::character varying
  */
      let dataToPost = [];

      selectedRows.map((row) => {
        if (row.payment_id) {
          dataToPost.push({
            payment_id: row?.payment_id,
            payment_date:
              selectedRows.length === 1
                ? row.payment_date
                : convertDateStringToIsoString(paymentDate),
            payment_type: paymentType,
            payment_status: paymentStatus,
            inspection_id: row?.inspection_id,
            inspection_type: row?.inspection_type,
            modified_by_user: reconciledBy,
            check_number: checkNumber,
            check_payee: checkPayee,
            payer_name: payerName,
          });
        }
      });

      // post data
      const _response = await postPaymentData(dataToPost);
      // console.log(_response);
    }
  };

  const clearData = (event) => {
    if (event) {
      event.preventDefault();
    }

    // clear values
    reconciledBy = "";
    setPayerName("");
    setPaymentType("");
    setPaymentStatus("");
    setPaymentDate("");
    setOpenPopup(false);
  };

  const convertDateStringToIsoString = (dateString) => {
    // console.log("Received date: ", dateString);

    var bits = dateString.split("-");

    // console.log("date bits 0, 1, 2: ", bits[0], bits[1], bits[2]);
    // below code adds 1 more month to ouput
    //return new Date(bits[0], bits[1], bits[2]).toISOString();
    // this one is correct
    return new Date(`${bits[0]} ${bits[1]} ${bits[2]} UTC`).toISOString();
  };

  const dataNotValid = () => {
    // This block will reset the errors and validate all again
    setErrors([]);
    let tmpErrors = [];
    // console.log("paymentType: ", paymentType);
    // console.log("paymentStatus:", paymentStatus);
    // console.log("checkNumber:", checkNumber);
    // console.log("checkPayee:", checkPayee);
    // console.log("payment Name", payerName);

    if (paymentType.toLowerCase() === "all") {
      tmpErrors.push({
        fieldName: "Payment Type",
        message: "cannot be 'All'",
      });
    }

    // payment type released_no_charge cannot be changed since we do not accept money
    // and also in the inspection records this has dependent items based on the type
    // so this should not be allowed
    if (
      paymentType.toLowerCase() === "released_at_no_charge" &&
      paymentType.toLowerCase() != initialPaymentType.toLowerCase()
    ) {
      tmpErrors.push({
        fieldName: "Payment Validation Rules",
        message: "Payment Type Released at no charge cannot be applied!",
      });
    }

    if (paymentType.toLowerCase() === "check") {
      if (checkNumber === "" || checkPayee === "") {
        tmpErrors.push({
          fieldName: "Payment Validation Rules",
          message:
            "Check payment type must have both Check Number and Check Payee",
        });
      }
      if (
        paymentStatus.toLowerCase() === "exempt" ||
        paymentStatus.toLowerCase() === "refunded"
      ) {
        tmpErrors.push({
          fieldName: "Payment Validation Rules",
          message: "Check payment type cannot have an Exempt or Refund status!",
        });
      }
    }

    if (
      paymentType.toLowerCase() === "pay_later" ||
      paymentType.toLowerCase() === "invoice"
    ) {
      if (payerName === "") {
        tmpErrors.push({
          fieldName: "Payment Validation Rules",
          message: "Must have a payer name",
        });
      }

      if (paymentStatus.toLowerCase() === "exempt") {
        tmpErrors.push({
          fieldName: "Payment Validation Rules",
          message: "pay_later cannot have an Exempt status",
        });
      }
    }

    if (paymentType.toLowerCase() === "released_at_no_charge") {
      if (paymentStatus.toLowerCase() !== "exempt") {
        tmpErrors.push({
          fieldName: "Payment Validation Rules",
          message: "Released at no charge must have an Exempt status",
        });
      }
    }

    if (tmpErrors && tmpErrors.length && tmpErrors.length > 0) {
      setErrors(tmpErrors);
      return true;
    }

    return false;
  };

  let renderError;
  if (errors && errors.length && errors.length > 0) {
    renderError = (
      <>
        <ShowErrors errors={errors} />{" "}
      </>
    );
  }

  let content;
  if (loading) {
    content = <LoadingIndicator />;
  } else {
    content = (
      <>
        {renderError}
        <form>
          <Grid
            container
            spacing={2}
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            style={{ marginLeft: "10px" }}
          >
            <Grid item>
              <Typography className={classes.labelHeight}>
                Payment Date
              </Typography>
              <TextField
                label=""
                name="paymentDate"
                id="paymentDate"
                type="date"
                disabled={true}
                // defaultValue="mm/dd/yyyy"
                fontSize="12"
                margin="none"
                //   inputProps={{
                //     min: "2022-01-01",
                //     style: { fontSize: 12, margin: 0 },
                //   }}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: 14, margin: 8 },
                }}
                padding="0px 0px 0px 0px"
                className={classes.dateTextFieldField}
                value={paymentDate}
                onChange={(e) => {}}
              />
            </Grid>
            <Grid item>
              {/* Payment type begin */}
              <FormControl
                className={classes.formControl}
                style={{ width: 200 }}
              >
                <InputLabel shrink id="paymentType">
                  Payment Type
                </InputLabel>
                <Select
                  labelId="paymentType"
                  name="paymentType"
                  id="demo-simple-select-placeholder-label"
                  value={paymentType}
                  onChange={(e) => {
                    handlePaymentTypeChange(e);
                  }}
                  displayEmpty
                  className={classes.selectEmpty}
                >
                  <MenuItem value="all">
                    <em>All</em>
                  </MenuItem>
                  {getPaymentTypeList().map((item, index) => {
                    return (
                      <MenuItem key={index} value={item.id}>
                        {item.title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {/* Payment Type end */}
            </Grid>
            <Grid item>
              {/* Payment Status begin */}
              <FormControl
                className={classes.formControl}
                style={{ width: 200 }}
              >
                <InputLabel shrink id="paymentStatus">
                  Payment Status
                </InputLabel>
                <Select
                  labelId="paymentStatus"
                  name="paymentStatus"
                  id="paymentStatus"
                  value={paymentStatus}
                  onChange={(e) => {
                    handlePaymentStatusChange(e);
                  }}
                  displayEmpty
                  className={classes.selectEmpty}
                >
                  <MenuItem value="all">
                    <em>All</em>
                  </MenuItem>
                  {getPaymentStatusList().map((item, index) => {
                    return (
                      <MenuItem key={index} value={item.title}>
                        {item.title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {/* Payment Status end */}
            </Grid>
            <Grid item>
              <Typography className={classes.labelHeight}>
                Payer Name
              </Typography>
              <TextField
                // id={id}
                name="payerName"
                id="payer_name"
                variant="outlined"
                value={payerName}
                onChange={(e) => {
                  handlePayerNameChange(e);
                }}
                size="small"
                className={classes.textField}
                style={{ marginRight: "0px" }}
                inputProps={{
                  maxLength: 20,
                  // labelHeight: 20,
                }}
                InputLabelProps={{
                  shrink: true,
                  style: {
                    fontSize: 14,
                    margin: 8,
                  },
                }}
              />
            </Grid>
            <Grid item>
              <Typography className={classes.labelHeight}>
                Check Number
              </Typography>
              <TextField
                // id={id}
                name="checkNumber"
                id="check_number"
                variant="outlined"
                value={checkNumber}
                onChange={(e) => {
                  handleCheckNumberChange(e);
                }}
                size="small"
                className={classes.textField}
                style={{ marginRight: "0px" }}
                inputProps={{
                  maxLength: 20,
                  // labelHeight: 20,
                }}
                InputLabelProps={{
                  shrink: true,
                  style: {
                    fontSize: 14,
                    margin: 8,
                  },
                }}
              />
            </Grid>
            <Grid item>
              <Typography className={classes.labelHeight}>
                Check Payee
              </Typography>
              <TextField
                // id={id}
                name="checkPayee"
                id="check_payee"
                variant="outlined"
                value={checkPayee}
                onChange={(e) => {
                  handleCheckPayeeChange(e);
                }}
                size="small"
                className={classes.textField}
                style={{ marginRight: "0px" }}
                inputProps={{
                  maxLength: 20,
                  // labelHeight: 20,
                }}
                InputLabelProps={{
                  shrink: true,
                  style: {
                    fontSize: 14,
                    margin: 8,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid container display="flex-end">
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                style={{
                  width: "80px",
                  height: "20px",
                  fontSize: "0.8rem",
                  padding: "10px 6px 8px 4px",
                  margin: "20px 10px 10px 6px",
                }}
                onClick={(e) => {
                  clearData(e);
                }}
              >
                Cancel
              </Button>
              <Button label="" style={{ backgroundColor: "white" }} />
              <Button label="" style={{ backgroundColor: "white" }} />
              <Button label="" style={{ backgroundColor: "white" }} />
              <Button
                variant="contained"
                color="primary"
                style={{
                  width: "80px",
                  height: "20px",
                  fontSize: "0.8rem",
                  padding: "10px 6px 8px 4px",
                  margin: "20px 10px 10px 16px",
                }}
                onClick={(e) => {
                  handleSubmit(e);
                }}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </>
    );
  }
  return <>{content}</>;
};

export default EditPayment;
